import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, Percent, Building2, Car, PiggyBank, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function CalculatorPage() {
  const navigate = useNavigate();
  const [activeCalc, setActiveCalc] = useState('vat');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50/30 to-orange-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <div className="border-b border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="gap-2 dark:text-slate-200">
              <ArrowLeft className="w-4 h-4" />
              Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <h1 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Kalkulator Podatkowy
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Calculator tabs */}
        <div className="flex gap-2 flex-wrap mb-8">
          {[
            { id: 'vat', label: 'VAT', icon: <Percent className="w-4 h-4" /> },
            { id: 'pit', label: 'PIT', icon: <PiggyBank className="w-4 h-4" /> },
            { id: 'zus', label: 'ZUS', icon: <Building2 className="w-4 h-4" /> },
            { id: 'car', label: 'Samochód', icon: <Car className="w-4 h-4" /> },
          ].map(tab => (
            <Button
              key={tab.id}
              data-testid={`calc-tab-${tab.id}`}
              onClick={() => setActiveCalc(tab.id)}
              variant={activeCalc === tab.id ? 'default' : 'outline'}
              className={`gap-2 ${activeCalc !== tab.id ? 'dark:border-slate-600 dark:text-slate-300' : ''}`}
            >
              {tab.icon}
              {tab.label}
            </Button>
          ))}
        </div>

        {activeCalc === 'vat' && <VatCalculator />}
        {activeCalc === 'pit' && <PitCalculator />}
        {activeCalc === 'zus' && <ZusCalculator />}
        {activeCalc === 'car' && <CarCalculator />}
      </div>
    </div>
  );
}

function CalcCard({ title, children }) {
  const resultRef = useRef(null);
  const handlePrint = () => {
    if (!resultRef.current) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>KAI - ${title}</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;color:#1e3a5f}
      h1{font-size:22px;margin-bottom:5px}h2{font-size:16px;color:#666;margin-bottom:20px}
      table{width:100%;border-collapse:collapse;margin-top:15px}
      td{padding:8px 12px;border-bottom:1px solid #eee;font-size:14px}
      td:last-child{text-align:right;font-weight:600}
      .footer{margin-top:30px;padding-top:15px;border-top:1px solid #ddd;font-size:11px;color:#999}
      </style></head><body>
      <h1>KAI - ${title}</h1>
      <h2>Data: ${new Date().toLocaleDateString('pl-PL')}</h2>
      ${resultRef.current.innerHTML}
      <div class="footer">Wygenerowano przez KAI (kaidoradza.pl) - informacja poglądowa, nie stanowi porady podatkowej.</div>
      </body></html>`);
    win.document.close();
    win.print();
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl text-primary-900 dark:text-slate-100 font-bold">{title}</h2>
        <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2 dark:border-slate-600 dark:text-slate-300" data-testid="print-calc">
          <Download className="w-4 h-4" />
          Drukuj
        </Button>
      </div>
      <div ref={resultRef}>{children}</div>
    </div>
  );
}

function ResultRow({ label, value, highlight }) {
  return (
    <div className={`flex justify-between items-center py-2 ${highlight ? 'font-bold text-lg' : 'text-sm'}`}>
      <span className="text-slate-600 dark:text-slate-400">{label}</span>
      <span className={highlight ? 'text-primary-900 dark:text-white' : 'text-slate-800 dark:text-slate-200'}>
        {typeof value === 'number' ? value.toFixed(2) + ' zł' : value}
      </span>
    </div>
  );
}

function VatCalculator() {
  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState('23');
  const [direction, setDirection] = useState('netto');

  const vatRate = parseFloat(rate) / 100;
  const val = parseFloat(amount) || 0;

  let netto, vat, brutto;
  if (direction === 'netto') {
    netto = val;
    vat = val * vatRate;
    brutto = val + vat;
  } else {
    brutto = val;
    netto = val / (1 + vatRate);
    vat = brutto - netto;
  }

  return (
    <CalcCard title="Kalkulator VAT">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <Label className="dark:text-slate-300">Kwota</Label>
            <Input
              data-testid="vat-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Wpisz kwotę..."
              className="mt-1 dark:bg-slate-700 dark:border-slate-600"
            />
          </div>
          <div>
            <Label className="dark:text-slate-300">Typ kwoty</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="netto">Netto (bez VAT)</SelectItem>
                <SelectItem value="brutto">Brutto (z VAT)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="dark:text-slate-300">Stawka VAT</Label>
            <Select value={rate} onValueChange={setRate}>
              <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="23">23% - podstawowa</SelectItem>
                <SelectItem value="8">8% - obniżona</SelectItem>
                <SelectItem value="5">5% - obniżona</SelectItem>
                <SelectItem value="0">0% - zerowa</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Wynik</h3>
          <div className="space-y-1 divide-y divide-slate-200 dark:divide-slate-600">
            <ResultRow label="Kwota netto" value={netto} />
            <ResultRow label={`VAT (${rate}%)`} value={vat} />
            <ResultRow label="Kwota brutto" value={brutto} highlight />
          </div>
        </div>
      </div>
    </CalcCard>
  );
}

function PitCalculator() {
  const [income, setIncome] = useState('');
  const [costs, setCosts] = useState('');
  const [form, setForm] = useState('skala');

  const gross = parseFloat(income) || 0;
  const deductions = parseFloat(costs) || 0;
  const base = Math.max(0, gross - deductions);

  let tax = 0, effectiveRate = 0;
  if (form === 'skala') {
    const freeAmount = 30000;
    const taxable = Math.max(0, base - freeAmount);
    if (taxable <= 120000) {
      tax = taxable * 0.12;
    } else {
      tax = 120000 * 0.12 + (taxable - 120000) * 0.32;
    }
  } else if (form === 'liniowy') {
    tax = base * 0.19;
  } else if (form.startsWith('ryczalt_')) {
    const rateMap = {
      'ryczalt_17': 0.17, 'ryczalt_15': 0.15, 'ryczalt_14': 0.14,
      'ryczalt_12': 0.12, 'ryczalt_10': 0.10, 'ryczalt_85': 0.085,
      'ryczalt_55': 0.055, 'ryczalt_3': 0.03, 'ryczalt_2': 0.02,
    };
    tax = gross * (rateMap[form] || 0.085);
  }
  effectiveRate = gross > 0 ? (tax / gross * 100) : 0;

  return (
    <CalcCard title="Kalkulator PIT">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <Label className="dark:text-slate-300">Przychód roczny</Label>
            <Input data-testid="pit-income" type="number" value={income} onChange={(e) => setIncome(e.target.value)} placeholder="Roczny przychód..." className="mt-1 dark:bg-slate-700 dark:border-slate-600" />
          </div>
          <div>
            <Label className="dark:text-slate-300">Koszty uzyskania przychodu</Label>
            <Input data-testid="pit-costs" type="number" value={costs} onChange={(e) => setCosts(e.target.value)} placeholder="Roczne koszty..." className="mt-1 dark:bg-slate-700 dark:border-slate-600" />
          </div>
          <div>
            <Label className="dark:text-slate-300">Forma opodatkowania</Label>
            <Select value={form} onValueChange={setForm}>
              <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="skala">Skala podatkowa (12%/32%)</SelectItem>
                <SelectItem value="liniowy">Podatek liniowy (19%)</SelectItem>
                <SelectItem value="ryczalt_17">Ryczałt 17% - wolne zawody</SelectItem>
                <SelectItem value="ryczalt_15">Ryczałt 15% - pośrednictwo</SelectItem>
                <SelectItem value="ryczalt_14">Ryczałt 14% - opieka zdrowotna</SelectItem>
                <SelectItem value="ryczalt_12">Ryczałt 12% - IT, doradztwo</SelectItem>
                <SelectItem value="ryczalt_10">Ryczałt 10% - nieruchomości</SelectItem>
                <SelectItem value="ryczalt_85">Ryczałt 8,5% - usługi, najem</SelectItem>
                <SelectItem value="ryczalt_55">Ryczałt 5,5% - budowlanka, produkcja</SelectItem>
                <SelectItem value="ryczalt_3">Ryczałt 3% - handel, gastronomia</SelectItem>
                <SelectItem value="ryczalt_2">Ryczałt 2% - produkty rolne</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Wynik</h3>
          <div className="space-y-1 divide-y divide-slate-200 dark:divide-slate-600">
            <ResultRow label="Przychód" value={gross} />
            {form.startsWith('ryczalt') ? null : <ResultRow label="Koszty" value={deductions} />}
            {form.startsWith('ryczalt') ? null : <ResultRow label="Dochód (podstawa)" value={base} />}
            <ResultRow label="Podatek PIT" value={tax} highlight />
            <ResultRow label="Efektywna stawka" value={`${effectiveRate.toFixed(1)}%`} />
            <ResultRow label="Netto (po podatku)" value={form.startsWith('ryczalt') ? gross - tax : gross - tax - deductions} highlight />
          </div>
        </div>
      </div>
    </CalcCard>
  );
}

function ZusCalculator() {
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [zusType, setZusType] = useState('pelny');
  const [taxForm, setTaxForm] = useState('skala');

  const income = parseFloat(monthlyIncome) || 0;

  // 2026 approximate bases
  const fullBase = 5203.80; // 60% of avg wage ~8673
  const prefBase = 1399.80; // 30% of min wage ~4666

  const base = zusType === 'pelny' ? fullBase : prefBase;

  const emerytalna = base * 0.1952;
  const rentowa = base * 0.08;
  const chorobowa = base * 0.0245;
  const wypadkowa = base * 0.0167;
  const fp = base * 0.0245;
  const fs = base * 0.0145;
  const socialTotal = emerytalna + rentowa + chorobowa + wypadkowa + fp + fs;

  // Health insurance
  let zdrowotna = 0;
  if (taxForm === 'skala') {
    zdrowotna = Math.max(income * 0.09, base * 0.09);
  } else if (taxForm === 'liniowy') {
    zdrowotna = Math.max(income * 0.049, base * 0.09);
  } else {
    // Ryczałt - simplified
    zdrowotna = income <= 5000 ? fullBase * 0.6 * 0.09 : income <= 25000 ? fullBase * 0.09 : fullBase * 1.8 * 0.09;
  }

  const total = socialTotal + zdrowotna;

  return (
    <CalcCard title="Kalkulator ZUS Przedsiębiorcy">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <Label className="dark:text-slate-300">Miesięczny dochód</Label>
            <Input data-testid="zus-income" type="number" value={monthlyIncome} onChange={(e) => setMonthlyIncome(e.target.value)} placeholder="Dochód miesięczny..." className="mt-1 dark:bg-slate-700 dark:border-slate-600" />
          </div>
          <div>
            <Label className="dark:text-slate-300">Rodzaj ZUS</Label>
            <Select value={zusType} onValueChange={setZusType}>
              <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pelny">Pełny ZUS</SelectItem>
                <SelectItem value="preferencyjny">Preferencyjny (pierwsze 24 mies.)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="dark:text-slate-300">Forma opodatkowania</Label>
            <Select value={taxForm} onValueChange={setTaxForm}>
              <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="skala">Skala podatkowa</SelectItem>
                <SelectItem value="liniowy">Podatek liniowy</SelectItem>
                <SelectItem value="ryczalt">Ryczałt</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Podstawa: {base.toFixed(2)} zł ({zusType === 'pelny' ? '60% przec. wynagr.' : '30% min. wynagr.'})</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Składki miesięczne</h3>
          <div className="space-y-1 divide-y divide-slate-200 dark:divide-slate-600">
            <ResultRow label="Emerytalna (19,52%)" value={emerytalna} />
            <ResultRow label="Rentowa (8%)" value={rentowa} />
            <ResultRow label="Chorobowa (2,45%)" value={chorobowa} />
            <ResultRow label="Wypadkowa (1,67%)" value={wypadkowa} />
            <ResultRow label="Fundusz Pracy (2,45%)" value={fp} />
            <ResultRow label="Fundusz Solidarn. (1,45%)" value={fs} />
            <ResultRow label="Zdrowotna" value={zdrowotna} />
            <ResultRow label="RAZEM miesięcznie" value={total} highlight />
            <ResultRow label="RAZEM rocznie" value={total * 12} highlight />
          </div>
        </div>
      </div>
    </CalcCard>
  );
}

function CarCalculator() {
  const [carValue, setCarValue] = useState('');
  const [isElectric, setIsElectric] = useState('no');
  const [usage, setUsage] = useState('mixed');

  const value = parseFloat(carValue) || 0;
  const limit = isElectric === 'yes' ? 225000 : 150000;
  const deductibleValue = Math.min(value, limit);
  const yearlyAmort = deductibleValue * 0.20;
  const vatDeduct = usage === 'business' ? value * 0.23 : value * 0.23 * 0.5;
  const expenseRate = usage === 'business' ? 1.0 : 0.75;

  return (
    <CalcCard title="Kalkulator Samochodu w Firmie">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <Label className="dark:text-slate-300">Wartość samochodu (netto)</Label>
            <Input data-testid="car-value" type="number" value={carValue} onChange={(e) => setCarValue(e.target.value)} placeholder="Wartość netto..." className="mt-1 dark:bg-slate-700 dark:border-slate-600" />
          </div>
          <div>
            <Label className="dark:text-slate-300">Typ pojazdu</Label>
            <Select value={isElectric} onValueChange={setIsElectric}>
              <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="no">Spalinowy / hybryda</SelectItem>
                <SelectItem value="yes">Elektryczny</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="dark:text-slate-300">Sposób użytkowania</Label>
            <Select value={usage} onValueChange={setUsage}>
              <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="business">Tylko firmowy (ewidencja przebiegu)</SelectItem>
                <SelectItem value="mixed">Mieszany (firmowy + prywatny)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Wynik</h3>
          <div className="space-y-1 divide-y divide-slate-200 dark:divide-slate-600">
            <ResultRow label="Limit amortyzacji" value={limit} />
            <ResultRow label="Wartość do amortyzacji" value={deductibleValue} />
            <ResultRow label="Roczny odpis (20%)" value={yearlyAmort} highlight />
            <ResultRow label="Odliczenie VAT" value={vatDeduct} />
            <ResultRow label="Wydatki eksploat." value={`${(expenseRate * 100).toFixed(0)}% w kosztach`} />
            {value > limit && (
              <ResultRow label="Nadwyżka (nie w kosztach)" value={value - limit} />
            )}
          </div>
        </div>
      </div>
    </CalcCard>
  );
}
